import { useState, useEffect, useCallback } from "react";
import { Check, X, Loader2, AlertTriangle, Link as LinkIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

// Brazilian Portuguese profanity list (common terms)
const PROFANITY_LIST = [
  "porra", "caralho", "merda", "foda", "fodase", "puta", "putaria",
  "cuzao", "cuzão", "buceta", "viado", "viada", "arrombado", "arrombada",
  "desgraca", "desgraça", "filhodaputa", "fdp", "piroca", "rola",
  "cacete", "bosta", "vagabunda", "vagabundo", "otario", "otária",
  "babaca", "imbecil", "idiota", "corno", "cornudo", "pau", "pinto",
  "xereca", "xoxota", "punheta", "punheteiro", "safado", "safada",
  "vadia", "vadio", "lazarento", "maldito", "maldita", "rapariga",
  "piranha", "galinha", "canalha", "cretino", "cretina",
];

const containsProfanity = (text: string): boolean => {
  const normalized = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
  return PROFANITY_LIST.some((word) => {
    const normalizedWord = word
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]/g, "");
    return normalized.includes(normalizedWord);
  });
};

export const generateSlugFromName = (name: string): string => {
  let slug = name.toLowerCase().trim();
  slug = slug.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  slug = slug.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return slug;
};

interface SlugEditorProps {
  value: string;
  onChange: (slug: string) => void;
  restaurantId?: string; // if editing, exclude current restaurant
  restaurantName?: string; // to auto-generate suggestions
  onValidityChange?: (isValid: boolean) => void;
  onProfanityDetected?: (detected: boolean) => void;
}

type SlugStatus = "idle" | "checking" | "available" | "taken" | "profanity" | "invalid";

const SlugEditor = ({
  value,
  onChange,
  restaurantId,
  restaurantName,
  onValidityChange,
  onProfanityDetected,
}: SlugEditorProps) => {
  const [status, setStatus] = useState<SlugStatus>("idle");
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const checkAvailability = useCallback(
    async (slug: string) => {
      if (!slug || slug.length < 3) {
        setStatus("invalid");
        onValidityChange?.(false);
        return;
      }

      if (containsProfanity(slug)) {
        setStatus("profanity");
        onValidityChange?.(true); // Allow submission — will be flagged for review
        onProfanityDetected?.(true);
        return;
      } else {
        onProfanityDetected?.(false);
      }

      setStatus("checking");
      setSuggestions([]);

      let query = supabase
        .from("restaurants")
        .select("id, slug")
        .eq("slug", slug);

      if (restaurantId) {
        query = query.neq("id", restaurantId);
      }

      const { data } = await query;

      if (data && data.length > 0) {
        setStatus("taken");
        onValidityChange?.(false);
        // Generate suggestions
        const sugList: string[] = [];
        const base = slug.replace(/-\d+$/, "");
        for (let i = 1; i <= 3; i++) {
          sugList.push(`${base}-${i}`);
        }
        if (restaurantName) {
          const city = `${base}-restaurante`;
          sugList.push(city);
        }
        setSuggestions(sugList.slice(0, 3));
      } else {
        setStatus("available");
        onValidityChange?.(true);
      }
    },
    [restaurantId, restaurantName, onValidityChange, onProfanityDetected]
  );

  // Debounce check
  useEffect(() => {
    if (!value) {
      setStatus("idle");
      return;
    }
    const timer = setTimeout(() => checkAvailability(value), 500);
    return () => clearTimeout(timer);
  }, [value, checkAvailability]);

  const handleChange = (raw: string) => {
    const slug = generateSlugFromName(raw);
    onChange(slug);
  };

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-1.5">
        <LinkIcon className="h-3.5 w-3.5" />
        URL do Cardápio Digital
      </Label>
      <div className="flex items-center gap-0">
        <span className="h-10 flex items-center px-3 bg-muted border border-r-0 border-border rounded-l-md text-xs text-muted-foreground whitespace-nowrap">
          {baseUrl}/
        </span>
        <Input
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="meu-restaurante"
          className="rounded-l-none h-10 bg-card border-border font-mono text-sm"
        />
      </div>

      {/* Status indicator */}
      <div className="flex items-center gap-1.5 text-xs">
        {status === "checking" && (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Verificando disponibilidade...</span>
          </>
        )}
        {status === "available" && (
          <>
            <Check className="h-3.5 w-3.5 text-emerald-500" />
            <span className="text-emerald-500 font-medium">URL disponível!</span>
          </>
        )}
        {status === "taken" && (
          <>
            <X className="h-3.5 w-3.5 text-destructive" />
            <span className="text-destructive font-medium">URL já em uso</span>
          </>
        )}
        {status === "profanity" && (
          <>
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-amber-500 font-medium">
              Conteúdo impróprio detectado — será enviado para revisão
            </span>
          </>
        )}
        {status === "invalid" && value && (
          <>
            <X className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">Mínimo 3 caracteres</span>
          </>
        )}
      </div>

      {/* Suggestions */}
      {status === "taken" && suggestions.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">Sugestões disponíveis:</p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onChange(s)}
                className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">
        Este será o link público do seu cardápio digital
      </p>
    </div>
  );
};

export default SlugEditor;
export { containsProfanity, PROFANITY_LIST };
